import { AlertCircle, AlertTriangle, CheckCircle, Download01, Eye, RefreshCcw01, SlashCircle01, Trash01, Upload01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import xlsxIcon from 'src/assets/img/FileTypeIcons/xlsx-lg.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CommonModal from 'src/components/CommonModal';
import CustomPagination from 'src/components/CustomPagination';
import FileDropzone from 'src/components/FileDropzone';
import TabButton from 'src/components/TabButton';
import TableCmp from 'src/components/TableCmp';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import type { BadgeType } from 'src/interface/common';
import { downloadAddressBookTemplate, importAddressBook } from 'src/services/AddressBookServices';
import WalToast from 'src/utils/WalToast';

import { IImportRecord, useAddressImportSocket } from '../hooks/useAddressImportSocket';

interface IProps {
    handleClose: (didImport: boolean) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;

    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_BADGE: Record<string, BadgeType> = {
    pending: 'gray',
    created: 'success',
    updated: 'blue',
    skipped: 'gray',
    failed: 'red',
};

const STATUS_CLASS: Record<string, string> = {
    pending: 'text-grayLight600',
    created: 'text-fgSuccessPrimary',
    updated: 'text-primary',
    skipped: 'text-grayLight600',
    failed: 'text-fgErrorPrimary',
};

const FIELD_SPEC: { labelKey: string; descKey: string; required: boolean }[] = [
    { labelKey: 'AddressBook.Import_Field_Index', descKey: 'AddressBook.Import_Field_Index_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_Company', descKey: 'AddressBook.Import_Field_Company_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_Type', descKey: 'AddressBook.Import_Field_Type_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_Address', descKey: 'AddressBook.Import_Field_Address_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_ContactName', descKey: 'AddressBook.Import_Field_ContactName_Desc', required: false },
    { labelKey: 'AddressBook.Import_Field_ContactNumber', descKey: 'AddressBook.Import_Field_ContactNumber_Desc', required: false },
    { labelKey: 'AddressBook.Import_Field_ContactEmail', descKey: 'AddressBook.Import_Field_ContactEmail_Desc', required: false },
    { labelKey: 'AddressBook.Import_Field_Hours', descKey: 'AddressBook.Import_Field_Hours_Desc', required: false },
];

const SERVICE_FIELD_SPEC: { labelKey: string; descKey: string; required: boolean }[] = [
    { labelKey: 'AddressBook.Import_Field_SvcIndex', descKey: 'AddressBook.Import_Field_SvcIndex_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_SvcName', descKey: 'AddressBook.Import_Field_SvcName_Desc', required: true },
    { labelKey: 'AddressBook.Import_Field_LimitedAccess', descKey: 'AddressBook.Import_Field_LimitedAccess_Desc', required: false },
];

const FieldList = ({ titleKey, fields }: { titleKey: string; fields: typeof FIELD_SPEC }) => {
    const { t } = useTranslation();

    return (
        <div>
            <p className="text-xs leading-[18px] font-medium text-textSecondary mb-1.5">{t(titleKey as any)}</p>
            <div className="border border-utilityGray200 rounded-lg bg-white shadow-sm p-3 flex flex-col gap-3">
                {fields.map((field) => (
                    <div key={field.labelKey} className="flex items-start gap-2">
                        <CheckCircle className={`w-4 h-4 flex-shrink-0 ${field.required ? 'text-success500' : 'text-warning500'}`} />
                        <div>
                            <p className="text-xs leading-[18px] font-medium text-textSecondary">{t(field.labelKey as any)}</p>
                            <p className="text-xs leading-[18px] font-normal text-gray500 mt-0.5">{t(field.descKey as any)}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StatCard = ({ label, value, icon, iconClass }: { label: string; value: number; icon?: React.ReactNode; iconClass?: string }) => (
    <div className="rounded-lg border border-utilityGray200 bg-white px-4 py-2.5 flex flex-col gap-1">
        <p className="text-xs leading-[18px] font-normal text-grayLight600">{label}</p>
        {/* Value grows to fill the row so the icon pins to the card's trailing edge. */}
        <div className="flex items-end gap-1.5 w-full">
            <p className="flex-1 min-w-0 text-sm font-medium text-grayLight900">{value}</p>
            {icon && <span className={`shrink-0 ${iconClass}`}>{icon}</span>}
        </div>
    </div>
);

const ImportRecordRow = ({ data }: { data: IImportRecord }) => {
    const { t } = useTranslation();
    const companyRef = useRef<HTMLParagraphElement>(null);
    const addressRef = useRef<HTMLParagraphElement>(null);
    const contactRef = useRef<HTMLParagraphElement>(null);

    const statusKey = `AddressBook.Import_${data.status.charAt(0).toUpperCase()}${data.status.slice(1)}` as any;
    const contact = [data.data?.contactName, data.data?.contactEmail, data.data?.contactNumber].filter(Boolean).join(' · ');
    const hours = data.data?.businessHoursStartTime && data.data?.businessHoursEndTime ? `${data.data.businessHoursStartTime} – ${data.data.businessHoursEndTime}` : '-';

    return (
        <>
            <td className="w-10 min-w-10 text-grayLight600">{data.data?.index ?? data.rowNumber}</td>
            <td className="max-w-[130px] min-w-[130px]">
                <TruncateTextCmp message={data.data?.companyName || '-'} refName={companyRef} tooltipType="action">
                    <p className="text-grayLight900 text-[12px] leading-[18px] block font-medium truncate" ref={companyRef}>
                        {data.data?.companyName || '-'}
                    </p>
                </TruncateTextCmp>
            </td>
            <td className="max-w-[100px] min-w-[100px]">
                <BadgeCmp style="modern" type={STATUS_BADGE[data.status] ?? 'gray'}>
                    {t(statusKey)}
                </BadgeCmp>
            </td>
            <td className="max-w-[150px] min-w-[150px]">
                <TruncateTextCmp message={data.data?.fullAddress || '-'} refName={addressRef} tooltipType="action">
                    <p className="text-grayLight600 font-normal truncate" ref={addressRef}>
                        {data.data?.fullAddress || '-'}
                    </p>
                </TruncateTextCmp>
            </td>
            <td className="max-w-[150px] min-w-[150px]">
                {contact ? (
                    <TruncateTextCmp message={contact} refName={contactRef} tooltipType="action">
                        <p className="text-grayLight600 font-normal truncate" ref={contactRef}>
                            {contact}
                        </p>
                    </TruncateTextCmp>
                ) : (
                    <span className="text-grayLight600">-</span>
                )}
            </td>
            <td className="min-w-[110px] max-w-[110px] whitespace-nowrap text-grayLight600">{hours}</td>
            <td className="min-w-[40px] w-[40px] max-w-[40px] sticky right-0 bg-inherit z-[1] table-cell-with-tooltip">
                <div className="flex items-center justify-end h-full table-col-tooltip-space-common-xs">
                    {data.status === 'failed' && data.error && (
                        <TooltipCmp message={data.error} position="top">
                            <AlertCircle className="w-4 h-4 text-fgErrorPrimary cursor-pointer" />
                        </TooltipCmp>
                    )}
                </div>
            </td>
        </>
    );
};

const ImportAddressModal = ({ handleClose }: IProps) => {
    const { t } = useTranslation();

    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [jobId, setJobId] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [initialRecords, setInitialRecords] = useState<IImportRecord[]>([]);

    const { records, progress, isComplete } = useAddressImportSocket(jobId, initialRecords);

    const isProcessing = Boolean(jobId) && !isComplete;
    const step = jobId ? 2 : 1;

    const counts = useMemo(() => {
        const tally = { created: 0, updated: 0, skipped: 0, failed: 0, pending: 0 };

        records.forEach((record) => {
            if (record.status === 'created') tally.created += 1;
            else if (record.status === 'updated') tally.updated += 1;
            else if (record.status === 'skipped') tally.skipped += 1;
            else if (record.status === 'failed') tally.failed += 1;
            else tally.pending += 1;
        });

        return tally;
    }, [records]);

    const RECORDS_PER_PAGE = 25;
    // Client-side: the full record list is already in the browser and the socket only patches
    // statuses by id, so pages are a slice of local state.
    const pagedRecords = useMemo(() => records.slice((currentPage - 1) * RECORDS_PER_PAGE, currentPage * RECORDS_PER_PAGE), [records, currentPage]);

    // Progress indicator only: no handleOnClick is wired, so the sidebar mirrors `step`
    // and never navigates. The second tab stays disabled until an import is running.
    const sidebarTabs = useMemo(
        () => [
            { value: 1, name: t('AddressBook.Import_Step_Upload'), icon: <Upload01 className="w-4 h-4" /> },
            { value: 2, name: t('AddressBook.Import_Step_Preview'), icon: <Eye className="w-4 h-4" />, disabled: step === 1 },
        ],
        [t, step]
    );

    const headCells = useMemo(
        () => [
            { id: 'index', name: '#' },
            { id: 'company', name: t('AddressBook.Import_Column_Company') },
            { id: 'status', name: t('AddressBook.Import_Column_Status') },
            { id: 'address', name: t('AddressBook.Address') },
            { id: 'contact', name: t('AddressBook.Import_Column_Contact') },
            { id: 'hours', name: t('AddressBook.Import_Column_Hours') },
            { id: 'action', name: '' },
        ],
        [t]
    );

    const handleFileSelect = useCallback(
        (selected: File | null) => {
            if (!selected) return;

            if (selected.size > MAX_FILE_SIZE) {
                setFileError(t('AddressBook.Import_File_Too_Large'));

                return;
            }

            setFileError('');
            setFile(selected);
        },
        [t]
    );

    const handleDownloadTemplate = async () => {
        setIsDownloading(true);

        try {
            await downloadAddressBookTemplate();
        } catch (error: any) {
            WalToast.error(error?.message || t('AddressBook.Template_Download_Failed'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        setIsUploading(true);
        setFileError('');

        try {
            const response: any = await importAddressBook(file);
            const { jobId: newJobId, records: parsedRecords } = response?.data ?? {};

            if (!newJobId) {
                setFileError(response?.message || t('AddressBook.Import_Start_Failed'));

                return;
            }

            setInitialRecords(parsedRecords ?? []);
            setJobId(newJobId);
        } catch (error: any) {
            // A rejected file arrives with its specific reason, which belongs on this step rather
            // than in a toast the user has to dismiss.
            setFileError(error?.response?.data?.message || error?.message || t('AddressBook.Import_Start_Failed'));
        } finally {
            setIsUploading(false);
        }
    };

    // Closing mid-run would abandon a job that keeps writing, so it is blocked until completion.
    const onClose = () => {
        if (isUploading || isProcessing) return;

        handleClose(Boolean(jobId));
    };

    return (
        <CommonModal
            title={t('AddressBook.Import_Addresses')}
            handleClose={onClose}
            size="max-w-[1400px] w-[calc(100%-2rem)] min-h-[88vh] max-h-[88vh]"
            ModalWithSidebar={true}
            ContentLeft={
                <TabButton
                    activeClassName="tab-box-vertical-active"
                    isVerticalBoxTab={true}
                    isModalTabVerticalSidebar={true}
                    disabledClassName="opacity-50 pointer-events-none cursor-not-allowed"
                    className="!cursor-default"
                    tabArray={sidebarTabs}
                    isActive={step}
                />
            }
            primaryBtnText={step === 1 ? t('AddressBook.Upload_And_Import') : isComplete ? t('AddressBook.Done') : ''}
            primaryBtnOnClick={step === 1 ? handleUpload : () => handleClose(true)}
            primaryBtnDisabled={step === 1 && (!file || isUploading)}
            primaryBtnLoading={isUploading}
            secondaryBtnText={isComplete ? '' : t('Common.Cancel')}
            secondaryBtnDisabled={isUploading || isProcessing}
            secondaryBtnOnClick={onClose}
            closeBtnClass={isUploading || isProcessing ? 'pointer-events-none opacity-50' : ''}
            modalClassName={step === 1 ? 'p-4 flex flex-col gap-4' : 'p-4 flex flex-col gap-3 min-h-0'}
            isOverflow={step === 1}
        >
            <>
                {step === 1 && (
                    <>
                        <div className="flex flex-col gap-1.5">
                            <p className="text-xs leading-[18px] font-medium text-grayLight900">
                                {t('AddressBook.Import_File')}
                                <span className="text-red-600 ms-[2px]">*</span>
                            </p>
                            <div className="rounded-lg border border-utilityGray200 bg-gray25 p-3">
                                {file ? (
                                    <div className="rounded-xl border border-borderPrimary bg-white flex items-start justify-between gap-3 px-4 py-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <img src={xlsxIcon} alt="" className="w-9 h-9 object-contain flex-none" />
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-textSecondary truncate">{file.name}</p>
                                                <p className="text-sm font-normal text-grayLight600">{formatFileSize(file.size)}</p>
                                            </div>
                                        </div>
                                        {!isUploading && (
                                            <TooltipCmp message={t('AddressBook.Remove')}>
                                                <ButtonCmp
                                                    type="button"
                                                    size="none"
                                                    className="text-grayLight600 flex-none"
                                                    ariaLabel={t('AddressBook.Remove')}
                                                    onClick={() => {
                                                        setFile(null);
                                                        setFileError('');
                                                    }}
                                                    icon={<Trash01 className="w-4 h-4" />}
                                                >
                                                    <></>
                                                </ButtonCmp>
                                            </TooltipCmp>
                                        )}
                                    </div>
                                ) : (
                                    <FileDropzone onFileSelect={handleFileSelect} disabled={isUploading} isError={Boolean(fileError)} />
                                )}
                            </div>
                        </div>

                        {fileError && (
                            <div className="flex items-start gap-1.5 text-fgErrorPrimary">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <p className="text-xs">{fileError}</p>
                            </div>
                        )}

                        <div>
                            <div className="flex justify-between items-center mb-1.5">
                                <h3 className="text-xs leading-[18px] font-semibold text-grayLight900">{t('AddressBook.Import_Expected_Format')}</h3>
                                <ButtonCmp
                                    type="button"
                                    className="btn-link-outline-primary"
                                    size="none"
                                    onClick={handleDownloadTemplate}
                                    disabled={isDownloading}
                                    icon={<Download01 className="w-4 h-4" />}
                                >
                                    {t('AddressBook.Download_Template')}
                                </ButtonCmp>
                            </div>
                            <div className="bg-utilityGray300 border border-utilityGray200 rounded-lg p-3 flex flex-col gap-3">
                                <FieldList titleKey="AddressBook.Import_Required_Fields" fields={FIELD_SPEC} />
                                <FieldList titleKey="AddressBook.Import_Services_Fields" fields={SERVICE_FIELD_SPEC} />
                            </div>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        {/* Live progress. It makes way for the cards once every row has settled,
                            leaving the completed screen 1:1 with the reference. */}
                        {!isComplete && (
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs leading-[18px] font-medium text-grayLight900">{t('AddressBook.Importing')}</p>
                                    <p className="text-xs leading-[18px] font-normal text-grayLight600">
                                        {t('AddressBook.Import_Progress_Of', {
                                            processed: progress?.processed ?? 0,
                                            total: progress?.total ?? records.length,
                                        })}
                                    </p>
                                </div>
                                <div className="w-full bg-utilityGray200 rounded-full h-1.5">
                                    <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${progress?.percentage ?? 0}%` }} />
                                </div>
                            </div>
                        )}

                        {/* One card per outcome, labelled and coloured exactly like the Status
                            column below so the two read as the same vocabulary. */}
                        <div className="grid grid-cols-5 gap-3">
                            <StatCard label={t('AddressBook.Import_Total_Records')} value={progress?.total ?? records.length} />
                            <StatCard label={t('AddressBook.Import_Created')} value={counts.created} icon={<CheckCircle className="w-3.5 h-3.5" />} iconClass={STATUS_CLASS.created} />
                            <StatCard label={t('AddressBook.Import_Updated')} value={counts.updated} icon={<RefreshCcw01 className="w-3.5 h-3.5" />} iconClass={STATUS_CLASS.updated} />
                            <StatCard label={t('AddressBook.Import_Skipped')} value={counts.skipped} icon={<SlashCircle01 className="w-3.5 h-3.5" />} iconClass={STATUS_CLASS.skipped} />
                            <StatCard label={t('AddressBook.Import_Failed')} value={counts.failed} icon={<AlertTriangle className="w-3.5 h-3.5" />} iconClass={STATUS_CLASS.failed} />
                        </div>

                        <div className="border border-utilityGray200 rounded-lg overflow-hidden flex flex-col flex-1 min-h-0">
                            <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-v2">
                                <TableCmp headCells={headCells} tableDataArr={pagedRecords} TableRowCmp={ImportRecordRow} tableRowHeight="xs" tableBodyRowClass="bg-white" isActionColumnSticky />
                            </div>
                            {records.length > RECORDS_PER_PAGE && (
                                <div className="w-full bg-white rounded-b-lg flex-shrink-0">
                                    <CustomPagination
                                        recordsPerPage={RECORDS_PER_PAGE}
                                        totalRecords={records.length}
                                        currentPage={currentPage}
                                        handlePagination={(page: number) => setCurrentPage(page)}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
            </>
        </CommonModal>
    );
};

export default ImportAddressModal;
